/**
 * Integration — one stored credential document per registry id, at
 * `workspaces/{orgId}/integrations/{id}`. `{id}` is an `IntegrationId`
 * (integrations.const.ts). Operator fields, provider-issued secrets, OAuth
 * tokens, the pending PKCE verifier and staged authorization code are ciphertext under the workspace's
 * data key (workspace-key.entity.ts), never plaintext and never a
 * `*Cipher`-named sibling field. Expiry and one-use claim metadata remain
 * ordinary values. Declares zero actions — no
 * `read`, no `write` of any kind — so the emitted rules carry no `match`
 * block for this path at all and the default-deny tail (`match
 * /{document=**} { allow read, write: if false; }`) covers it: no client
 * can ever read or write these docs; only the Admin SDK touches them. See
 * workspace.entity.ts's WorkspaceSettings for the member-readable status
 * blocks (verification facts, never a value) the Integrations surface
 * renders instead.
 *
 * One generic shape for every id — which keys `fields` carries is the
 * registry entry's `secrets` declaration, not a per-provider schema here.
 *
 * Fields justified:
 *   fields   — declared-field-name → ciphertext, one entry per field the
 *              id's registry entry declares: the operator-typed
 *              credentials.
 *   issued   — provider-issued secrets → ciphertext: webhook signing
 *              secrets a provider mints when a save registers its webhook
 *              — never operator-typed, so never a declared registry field,
 *              and keyed per registration where the provider issues one
 *              secret per registered endpoint; nullish — most
 *              integrations have no provider-issued secrets.
 *   previous — the `fields` map one generation back, kept so a save that
 *              stores a broken credential can be rolled back; nullish — a
 *              first-generation document has no prior generation.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { z } from "zod";
import { type Actor, Id, Sha256 } from "./workspace.entity.ts";

export const RevolutConnectionReturnUrlSchema = z
	.string()
	.url()
	.refine((value) => {
		if (!URL.canParse(value)) return false;
		const url = new URL(value);
		return (
			!url.username &&
			!url.password &&
			(url.protocol === "https:" ||
				(url.protocol === "http:" &&
					["localhost", "127.0.0.1", "[::1]"].includes(url.hostname)))
		);
	}, "expected an HTTPS or loopback return URL");

// Tokens and the PKCE verifier are ciphertext under the workspace key. Expiry
// and leases are epoch milliseconds so the server can compare them atomically.
// Excluded: provider profile data and a second organization id; the path owns it.
export const IntegrationOAuthSchema = z
	.object({
		accessToken: z.string().min(1),
		refreshToken: z.string().min(1),
		expiresAt: z.number().int().positive(),
		reconnectRequired: z.boolean(),
		refreshLease: z
			.object({ id: Id, until: z.number().int().positive() })
			.strict()
			.nullish(),
	})
	.strict();
export type IntegrationOAuth = z.infer<typeof IntegrationOAuthSchema>;

export const PendingRevolutConnectionSchema = z
	.object({
		stateHash: Sha256,
		uid: Id,
		verifier: z.string().min(1),
		expiresAt: z.number().int().positive(),
		returnUrl: RevolutConnectionReturnUrlSchema,
		currency: z.string().min(1).nullish(),
		// justifies: the one-use OAuth callback claimant, distinct from the mode confirmation.
		claimId: Id.nullish(),
		// justifies: the existing live-to-sandbox confirmation reserved at begin.
		sandboxClaimId: Id.nullish(),
		// justifies: held only until the initiating member finishes in an authenticated session.
		authorizationCode: z.string().min(1).nullish(),
		// justifies: a fresh completion nonce is spent by the authenticated finish, never the original state.
		completionHash: Sha256.nullish(),
	})
	.strict()
	.superRefine((pending, context) => {
		if (Boolean(pending.authorizationCode) !== Boolean(pending.completionHash))
			context.addIssue({
				code: "custom",
				path: ["completionHash"],
				message:
					"staged authorization requires both its encrypted code and completion nonce hash",
			});
	});
export type PendingRevolutConnection = z.infer<
	typeof PendingRevolutConnectionSchema
>;

export const IntegrationSchema = z
	.object({
		fields: z
			.record(z.string().min(1), z.string().min(1))
			.describe(
				"the integration's declared secret fields, each value ciphertext under the workspace key",
			),
		issued: z
			.record(z.string().min(1), z.string().min(1))
			.nullish()
			.describe(
				"the integration's provider-issued secrets — webhook signing secrets captured at registration, never operator-typed — each value ciphertext under the workspace key",
			),
		previous: z
			.record(z.string().min(1), z.string().min(1))
			.nullish()
			.describe(
				"the fields map one generation back, ciphertext, kept for rollback",
			),
		oauth: IntegrationOAuthSchema.nullish(),
		pending: PendingRevolutConnectionSchema.nullish(),
	})
	.superRefine((integration, context) => {
		if (integration.oauth && Object.keys(integration.fields).length > 0)
			context.addIssue({
				code: "custom",
				path: ["fields"],
				message: "OAuth and operator-supplied credentials cannot coexist",
			});
	});
export type Integration = z.infer<typeof IntegrationSchema>;

export const Integration = entity<Actor>()({
	schema: IntegrationSchema,
	actions: [] as const,
})
	.pipe(() => [])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/integrations/{id}",
		}),
	);
